/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import styled from "styled-components";
import { useValidator, validPattern } from "../../../utils/validators";
import { lang } from "../../../constants";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTextArea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => !['borderColor', 'fontSize', 'fontWeight', 'backgroundColor', 'borderRadius', 'width', 'labelColor', 'labelFontSize', 'labelFontWeight'].includes(prop as string),
})<{
  borderColor?: string;
  fontSize?: number;
  fontWeight?: number;
  backgroundColor?: string;
  borderRadius?: number;
  width?: any;
  labelColor?: string;
  labelFontSize?: number;
  labelFontWeight?: number;
}>`
  border: 1px solid ${props => props.borderColor || '#ccc'};
  font-size: ${props => props.fontSize ? `${props.fontSize}px` : '14px'};
  font-weight: ${props => props.fontWeight || 400};
  background-color: ${props => props.backgroundColor || 'inherit'};
  border-radius: ${props => props.borderRadius ? `${props.borderRadius}px` : '4px'};
  width: ${props => props.width === "100" ? '100%' : props.width};
  padding: 12px;
  resize: none;
  font-family: inherit;
  line-height: 1.5;
  overflow-y: auto;
  transition: height 0.1s ease;
  min-height: 150px;
  height:auto;

  &::placeholder {
    color: ${props => props.labelColor || '#B0B0B0'};
    font-size: ${props => props.labelFontSize ? `${props.labelFontSize}px` : '14px'};
    font-weight: ${props => props.labelFontWeight || 400};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.borderColor || '#e39435'};
    box-shadow: 0 0 0 2px rgba(227, 148, 53, 0.2);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 1360px) {
    width: 100%;
  }
`;


interface CustomTextAreaProps {
  titleEN?: string;
  titleCN?: string;
  placeHolder?: string;
  placeholder?: string;
  value: string;
  callback: (value: string) => void;
  minLength?: number;
  maxLength?: number;
  patternType?: string;
  isRequired?: boolean;
  minRows?: number;
  maxRows?: number;
  borderColor?: string;
  fontSize?: number;
  fontWeight?: number;
  bgColor?: string;
  color?: string;
  disabled?: boolean;
  width?: string;
  labelColor?: string;
  labelFontSize?: number;
  labelFontWeight?: number;
}

// Custom hook for auto-resize functionality
const useAutoResize = (
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  value: string,
  minRows: number = 5,
  maxRows: number = 10
) => {
  React.useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    
    // Calculate line height
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseInt(computedStyle.lineHeight) || 20;
    const paddingTop = parseInt(computedStyle.paddingTop) || 0;
    const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
    const borderTop = parseInt(computedStyle.borderTopWidth) || 0;
    const borderBottom = parseInt(computedStyle.borderBottomWidth) || 0;
    
    // Calculate min and max heights
    const minHeight = (lineHeight * minRows) + paddingTop + paddingBottom + borderTop + borderBottom;
    const maxHeight = (lineHeight * maxRows) + paddingTop + paddingBottom + borderTop + borderBottom;
    
    // Set the height based on content
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [value, minRows, maxRows]);
};

const CustomTextArea: React.FC<CustomTextAreaProps> = (props) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  
  // Use custom auto-resize hook
  useAutoResize(textareaRef, props.value, props.minRows || 5, props.maxRows || 10);

  const validators = {
    field: (value: string) => check(value, props.minLength, props.maxLength),
  };

  const { values, handleChange } = useValidator(
    {
      field: { value: props.value, accTxtBoxisValid: true, isTouched: false },
    },
    validators
  );

  const check = (value: string, minLength?: number, maxLength?: number) => {
    if (props.isRequired && (!value || value.trim() === "")) {
      return false;
    } else if (minLength && value.length < minLength) {
      return false;
    } else if (maxLength && value.length > maxLength) {
      return false;
    } else if (props.patternType && props.patternType !== "text") {
      // For non-text patterns, use global validator
      return validPattern(value);
    } else {
      return true;
    }
  };

  const keyUp = (_event: React.KeyboardEvent<HTMLTextAreaElement>) => {
  };

  const handleTextareaChange = (_event: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = _event.target.value;

    // Apply special character filtering for text inputs (same regex as AddNewBundleDeal)
    if (props.patternType === "text" || !props.patternType) {
      value = value.replace(/[^a-zA-Z0-9\s\-'.,()&]/g, '');
    }

    // Pass only the required target fields to the validator to avoid SyntheticEvent pooling issues
    const syntheticEvent = {
      target: {
        name: "field",
        value,
      },
    } as unknown as React.ChangeEvent<HTMLTextAreaElement>;
    
    handleChange(syntheticEvent);

    // Always propagate sanitized value; validation can display errors but should not block typing
    props.callback(value);
  };

  return (
    <Container>
      <StyledTextArea
        ref={textareaRef}
        value={values.field.value}
        name="field"
        onChange={handleTextareaChange}
        onKeyUp={keyUp}
        disabled={props.disabled}
        placeholder={props.placeholder || (lang() === "en" || lang() === "" || lang() === "en-US" ? props.titleEN : props.titleCN)}
        borderColor={props.borderColor}
        fontSize={props.fontSize}
        fontWeight={props.fontWeight}
        backgroundColor="inherit"
        borderRadius={4}
        width={props.width}
        labelColor={props.labelColor}
        labelFontSize={props.labelFontSize}
        labelFontWeight={props.labelFontWeight}
      />
      
      {/* {errors.field && values.field.isTouched ? (
        <ErrorMessage>
          {lang() === "en" || lang() === "" || lang() === "en-US"
            ? `Please enter a valid ${props.titleEN}.`
            : `请输入有效的${props.titleCN}。`}
        </ErrorMessage>
      ) : (
        <EmptySpace />
      )} */}
    </Container>
  );
}

export default CustomTextArea;